// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface OwnProps {
    isAuthorized?: boolean;
    isBrandClickable?: boolean;
    extendedStyles?: LegacyManufacturerStyles;
    legacyManufacturer?: string;
    legacyManufacturerLink?: string;
}

type Props = OwnProps;

export interface LegacyManufacturerStyles {
    nameLink?: LinkPresentationProps;
    nameText?: TypographyProps;
    brandNotClickable?: TypographyProps;
    labelText?: TypographyProps;
    wrapper?: InjectableCss;
}

export const legacyManufacturerStyles: LegacyManufacturerStyles = {};

const LegacyManufacturer: FC<Props> = ({
    extendedStyles,
    isAuthorized = true,
    isBrandClickable = false,
    legacyManufacturer,
    legacyManufacturerLink,
}) => {
    const [styles] = React.useState(() => mergeToNew(legacyManufacturerStyles, extendedStyles));

    const brandDisplay = legacyManufacturerLink ? (
        legacyManufacturer
    ) : (
        <Typography {...styles.nameText} data-test-selector="legacyManufacturer">
            {legacyManufacturer}
        </Typography>
    );

    return (
        <StyledWrapper {...styles.wrapper} as="p" data-test-selector="ProductFlipLegacyManufacturer">
            <Typography {...styles.labelText}>{translate("Legacy Manufacturer")}</Typography>
            {legacyManufacturerLink && isBrandClickable ? (
                <Link
                    {...styles.nameLink}
                    href={legacyManufacturerLink}
                    data-test-selector="brandLink"
                    {...{ isAuthorized }}
                >
                    {brandDisplay}
                </Link>
            ) : legacyManufacturerLink && !isBrandClickable ? (
                <Typography
                    {...styles.brandNotClickable}
                    data-test-selector="brandLinkNotClickable"
                    {...{ isAuthorized }}
                >
                    {brandDisplay}
                </Typography>
            ) : (
                <>{brandDisplay}</>
            )}
        </StyledWrapper>
    );
};

export default LegacyManufacturer;
